import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { MemberInfoValue } from '../../shared/component/headless/input/InputBody';
import { BlackFitBtn } from '../../shared/component/headless/button/BtnBody';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { Header as HeaderRow, Title } from '../../shared/component/modal/SearchModalLayout';
import deleteIcon from '../../asset/icon/delete.png';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { getHintMessage } from '../../shared/constants/hintMessages';
import { PROJECT_INFO_URL_MAX_LENGTH } from '../../shared/constants/limits';
import { AlertPosition, AlertType, TextAlignment } from '../../shared/enum/EnumRepository';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { isValidProjectInfoUrl } from '../../shared/util/projectInfo';

interface ProjectInfoUrlModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const ProjectInfoUrlModal: React.FC<ProjectInfoUrlModalProps> = ({ setIsUserInput }) => {
  const { standardModal, clearModal } = useModalStore();
  const enqueueAlert = useStandardAlertStore((s) => s.enqueueAlert);
  const [url, setUrl] = useState(standardModal?.value ?? '');

  useEffect(() => {
    setIsUserInput(url.length > 0);
  }, [url, setIsUserInput]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      setIsUserInput(false);
    };
  }, [clearModal, setIsUserInput]);

  const handleConfirm = (event: React.FormEvent) => {
    event.preventDefault();
    const nextUrl = url.trim();
    if (!isValidProjectInfoUrl(nextUrl)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_PROJECT_INFO_URL_FORMAT'),
      });
      return;
    }
    standardModal?.handleChange(nextUrl);
    clearModal();
  };

  return (
    <Container onSubmit={handleConfirm} noValidate>
      <HeaderRow>
        <Title>{getHintMessage('CONNECT_URL')}</Title>
        <UniversalIconBtn
          size="small"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          onClick={() => clearModal()}
          aria-label="Close"
        />
      </HeaderRow>
      <HeadlessInput
        type="url"
        value={url}
        handleChange={(event) => setUrl(event.target.value)}
        placeholder="https://www.architrave.work/"
        StyledInput={UrlInput}
        $alignment={TextAlignment.LEFT}
      />
      <BtnContainer>
        <BlackFitBtn type="submit">{getBtnName('OK')}</BlackFitBtn>
      </BtnContainer>
    </Container>
  );
};

export default ProjectInfoUrlModal;

const Container = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0.5vw;
`;

const UrlInput = styled(MemberInfoValue).attrs({
  'aria-label': 'URL',
  autoFocus: true,
  maxLength: PROJECT_INFO_URL_MAX_LENGTH,
})``;

const BtnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2.5vh;
`;
